import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { trackOnce } from "../lib/pixel";

const AuthContext = createContext(null);

export const roleHome = (role) =>
  role === "super_admin" ? "/admin" : role === "manager" ? "/manager" : "/investor";

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const trackRegistration = (data) => {
    if (data?.access_token && data?.user?.user_id) trackOnce("CompleteRegistration", data.user.user_id);
  };

  const saveTokens = useCallback((data) => {
    if (data.access_token) localStorage.setItem("tv_access", data.access_token);
    if (data.refresh_token) localStorage.setItem("tv_refresh", data.refresh_token);
    if (data.user) setUser(data.user);
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    saveTokens(data);
    return data.user;
  };

  // ─── Passwordless: Phone (WhatsApp via AiSensy) ────────────────────────
  const requestPhoneOtp = async (phone, name) =>
    (await api.post("/auth/phone/request-otp", { phone, name })).data;

  const verifyPhoneOtp = async (phone, code) => {
    const { data } = await api.post("/auth/phone/verify-otp", { phone, code });
    if (!data.new_user && data.access_token) saveTokens(data);
    return data;
  };

  const completePhoneSignup = async (payload) => {
    const { data } = await api.post("/auth/phone/complete-signup", payload);
    if (data.access_token) saveTokens(data);
    trackRegistration(data);
    return data;
  };

  // ─── Passwordless: Email (via existing Resend) ─────────────────────────
  const requestEmailOtp = async (email) =>
    (await api.post("/auth/email/request-otp", { email })).data;

  const verifyEmailOtp = async (email, code) => {
    const { data } = await api.post("/auth/email/verify-otp", { email, code });
    if (!data.new_user && data.access_token) saveTokens(data);
    return data;
  };

  const completeEmailSignup = async (payload) => {
    const { data } = await api.post("/auth/email/complete-signup", payload);
    if (data.access_token) saveTokens(data);
    trackRegistration(data);
    return data;
  };

  const register = async (name, email, password) => {
    // Returns { otp_required, email } — user must next verify via OTP
    const { data } = await api.post("/auth/register", { name, email, password });
    return data;
  };

  const verifyOtp = async (email, code, purpose = "register") => {
    const { data } = await api.post("/auth/otp/verify", { email, code, purpose });
    if (purpose === "register" && data.access_token) { saveTokens(data); trackRegistration(data); }
    return data;
  };

  const resendOtp = async (email, purpose = "register") =>
    (await api.post("/auth/otp/resend", { email, purpose })).data;

  const forgotPassword = async (email) =>
    (await api.post("/auth/forgot-password", { email })).data;

  const resetPassword = async (email, code, password) =>
    (await api.post("/auth/reset-password", { email, code, password })).data;

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {}
    localStorage.removeItem("tv_access");
    localStorage.removeItem("tv_refresh");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, register, logout,
                                     verifyOtp, resendOtp, forgotPassword, resetPassword, saveTokens,
                                     requestPhoneOtp, verifyPhoneOtp, completePhoneSignup,
                                     requestEmailOtp, verifyEmailOtp, completeEmailSignup }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
